import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const useGeolocation = (token) => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'

  // Check permission status
  useEffect(() => {
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => setPermissionStatus(result.state);
      }).catch(() => {
        // Some browsers don't support permission query for geolocation
      });
    }
  }, []);

  // Request location from browser
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation er ikke understøttet af din browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({ latitude, longitude });
        
        // Send to backend if we have a token
        if (token) {
          try {
            await axios.post(`${API}/location`, 
              { latitude, longitude },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error('Kunne ikke opdatere lokation på server:', err);
          }
        }
        
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Lokationsadgang blev nægtet');
            setPermissionStatus('denied');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Lokation kunne ikke bestemmes');
            break;
          case err.TIMEOUT:
            setError('Anmodning om lokation timeout');
            break;
          default:
            setError('En ukendt fejl opstod');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes cache
      }
    );
  }, [token]);

  // Load saved location from backend
  const loadSavedLocation = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await axios.get(`${API}/location`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.latitude && res.data.longitude) {
        setLocation({
          latitude: res.data.latitude,
          longitude: res.data.longitude
        });
      }
    } catch (err) {
      console.error('Kunne ikke hente gemt lokation:', err);
    }
  }, [token]);

  return {
    location,
    loading,
    error,
    permissionStatus,
    requestLocation,
    loadSavedLocation
  };
};

// Helper to format distance
export const formatDistance = (distanceKm) => {
  if (distanceKm === null || distanceKm === undefined) {
    return null;
  }
  
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m væk`;
  }
  
  if (distanceKm < 10) {
    return `${distanceKm.toFixed(1)} km væk`;
  }
  
  return `${Math.round(distanceKm)} km væk`;
};

export default useGeolocation;
